import React from 'react';
import { useQuery, gql } from '@apollo/client';

import { useHistory, useParams, Route, Switch } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';

import {
  PAYMENT_AUTOPAY_PATH,
  PAYMENT_ONE_TIME_PATH,
  PAYMENT_SPECIAL_INVOICE_PATH,
  CHANGE_DUE_DATE_PATH,
  BILLING_PREFERENCES_PATH,
} from '../constants/routes';
import { useHybridAuthToken } from '../components/hybridAuthToken';
import { useUserAuthInfo } from '../components/auth';
import OneTimePaymentV2 from './v2/OneTimePayment';
import AutopayEnrollmentV2 from './v2/AutopayEnrollment';
import ChangeDueDate from '../payment/v2/ChangeDueDate';
import BillingPreferences from '../payment/v2/BillingPreferences';
import PropTypes from 'prop-types';
import { usePaperBillFeeFlag } from '../hooks/useGetFeatureFlag';

const PAYMENT_PAGE_QUERY = gql`
  query GetFieldsForCybersourceQuery($prospectId: String!, $contactId: ID!, $hybridAuthToken: String) {
    contact(id: $contactId) {
      id
      firstName
      lastName
      email
      mobilePhone
      phone
    }
    prospect(prospectId: $prospectId) {
      id
      street
      city
      state
      zipCode
      billingSystem
      isVivintSolarPostPTO
      autopayByCreditCardEligible
      purchasedThru
      contractId
      paperBillFeeWaived
      billingNotificationPreference
      serviceContractNumber
      billing(contactId: $contactId, hybridAuthToken: $hybridAuthToken) {
        id
        totalAmountDue
        lastDueDate
        listOfDueDates
        lastDueDateUpdate
      }
      PTO
      parentProposalCreatedDate
      fundBridge
    }
  }
`;

const PaymentPage = ({ setCurrentLastDueDateUpdate }) => {
  const hybridAuthToken = useHybridAuthToken();
  const { isBillingEscalations } = useUserAuthInfo();
  const { prospectId, contactId } = useParams();
  const history = useHistory();
  const { loading, data, refetch } = useQuery(PAYMENT_PAGE_QUERY, {
    variables: { prospectId, contactId, hybridAuthToken },
    skip: !hybridAuthToken,
  });
  const { enabled: isPaperBillFeeEnabled, allowedStates, feeStartDate } = usePaperBillFeeFlag();

  if (loading) return <LinearProgress />;

  const specialInvoiceAmount = history?.location?.state?.specialInvoiceAmount ?? null;
  const referenceNumberPrefix = history?.location?.state?.referenceNumberPrefix ?? null;

  const { firstName, lastName, email, mobilePhone, phone } = data?.contact || {};
  const {
    street,
    city,
    state,
    zipCode,
    fundBridge,
    billingSystem,
    isVivintSolarPostPTO,
    autopayByCreditCardEligible,
    purchasedThru,
    contractId,
    paperBillFeeWaived,
    billingNotificationPreference,
    serviceContractNumber,
  } = data?.prospect || {};

  const showBillingPreferences = isPaperBillFeeEnabled;

  const parentProposalCreatedDate = data?.prospect?.parentProposalCreatedDate;
  const upperState = (state ?? '').toUpperCase();
  const isStateInAllowedList = allowedStates.some((s) => s.toUpperCase() === 'ALL' || s.toUpperCase() === upperState);
  const isContractAfterFeeStart =
    !feeStartDate ||
    !parentProposalCreatedDate ||
    new Date(parentProposalCreatedDate).getTime() >= new Date(feeStartDate).getTime();
  const includedInFee =
    isPaperBillFeeEnabled && (!parentProposalCreatedDate || (isStateInAllowedList && isContractAfterFeeStart));

  const { totalAmountDue, lastDueDate, listOfDueDates, lastDueDateUpdate } = data?.prospect?.billing || {};
  const userInfo = {
    prospectId,
    contactId,
    contractId,
    firstName,
    lastName,
    email,
    mobilePhoneNumber: mobilePhone,
    phoneNumber: phone,
    address: street,
    city,
    state,
    zip: zipCode,
    lastDueDate,
    listOfDueDates,
    lastDueDateUpdate,
    totalAmountDue: String(totalAmountDue),
    fundBridge,
    billingSystem,
    isVivintSolarPostPTO,
    autopayByCreditCardEligible,
    purchasedThru,
    ptoDate: data?.prospect.PTO,
    paperBillFeeWaived,
    billingNotificationPreference,
    serviceContractNumber,
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Switch>
              <Route path={PAYMENT_AUTOPAY_PATH}>
                <AutopayEnrollmentV2 userInfo={userInfo} includedInFee={includedInFee} />
              </Route>
              <Route path={PAYMENT_ONE_TIME_PATH}>
                <OneTimePaymentV2 userInfo={userInfo} includedInFee={includedInFee} />
              </Route>
              <Route path={PAYMENT_SPECIAL_INVOICE_PATH}>
                <OneTimePaymentV2
                  userInfo={userInfo}
                  specialInvoiceAmount={specialInvoiceAmount}
                  referenceNumberPrefix={referenceNumberPrefix}
                  isBillingEscalations={isBillingEscalations}
                  includedInFee={includedInFee}
                />
              </Route>
              <Route path={CHANGE_DUE_DATE_PATH}>
                <ChangeDueDate userInfo={userInfo} setCurrentLastDueDateUpdate={setCurrentLastDueDateUpdate} />
              </Route>
              {showBillingPreferences && (
                <Route path={BILLING_PREFERENCES_PATH}>
                  <BillingPreferences userInfo={userInfo} refetchUserInfo={refetch} />
                </Route>
              )}
            </Switch>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

PaymentPage.propTypes = {
  setCurrentLastDueDateUpdate: PropTypes.func,
};

export default PaymentPage;
